import { Col, Row } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Form from "../../form-components/from";
import { INITIAL_PAGE } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import {
  CampaignStatus,
  PeriodicType,
  getCampaignStatusName,
  getPeriodicTypeName,
  getDonationGoalName,
  DonationGoal
} from "../../../models/campaign/enums";
import CampaignContext from "../../../context/campaign/context";
import EndPoints from "../../../services/end-points";

interface IProps { }

const CampaignFilter: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(CampaignContext);
  const [isPeriodic, setIsPeriodic] = useState<boolean | null>(null);
  const [charities, setCharities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCharities, setLoadingCharities] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    const fetchCharities = async () => {
      setLoadingCharities(true);
      try {
        const response = await EndPoints.charity.getAllCharities({ light: true, perPage: -1 });
        setCharities(
          response.data.data.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching charities:", error);
      } finally {
        setLoadingCharities(false);
      }
    };
    fetchCharities();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await EndPoints.categoryCampaign.getAllCategoriesCampaigns({
          light: true,
          perPage: -1,
        });
        setCategories(
          response.data.data.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  return (
    <Form
      formId="campaign-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: data?.keyword,
          filters: [
            data?.status
              ? {
                name: "status",
                value: data.status,
                operation: "eq",
              }
              : undefined,
            isPeriodic === true
              ? {
                name: "periodic",
                value: 1,
                operation: "eq",
              }
              : isPeriodic === false
                ? {
                  name: "periodic",
                  value: 0,
                  operation: "eq",
                }
                : undefined,
            data?.donationGoal
              ? {
                name: "donationGoal",
                value: data.donationGoal,
                operation: "eq",
              }
              : undefined,
            data?.charityId
              ? {
                name: "charityId",
                value: data.charityId,
                operation: "eq",
              }
              : undefined,
            data?.categoryId
              ? {
                name: "categoryId",
                value: data.categoryId,
                operation: "eq",
              }
              : undefined,
            data?.periodicType && data.periodicType.length > 0
              ? {
                name: "periodicType",
                value: data.periodicType,
                operation: "eq", 
              }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <Row justify={"start"} gutter={[10, 0]}>
        <Col span={8}>
          <FieldBuilder
            name="status"
            label={`${t("status")}`}
            width="large"
            input={{
              type: "select",
              options: Object.values(CampaignStatus)
                .filter((value) => !isNaN(Number(value)))
                .map((status) => ({
                  label: t(getCampaignStatusName(status as CampaignStatus)),
                  value: status as number,
                })),
            }}
          />
        </Col>
        <Col span={8}>
          <FieldBuilder
            name="campaignType"
            label={`${t("campaign_type")}`}
            width="large"
            input={{
              type: "select",
              options: [
                { label: t("periodic"), value: true },
                { label: t("non_periodic"), value: false },
              ],
              onChange: (value) => setIsPeriodic(value),
            }}
          />
        </Col>
        {isPeriodic && (
          <Col span={8}>
            <FieldBuilder
              name="periodicType"
              label={`${t("periodic_type")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: Object.values(PeriodicType)
                  .filter((value) => !isNaN(Number(value)))
                  .map((period) => ({
                    label: t(getPeriodicTypeName(period as PeriodicType)),
                    value: period as number,
                  })),
              }}
            />
          </Col>
        )}
        <Col span={8}>
          <FieldBuilder
            name="donationGoal"
            label={t("donation_goal_label")}
            width="large"
            input={{
              type: "select",
              options: Object.values(DonationGoal)
                .filter((value) => !isNaN(Number(value)))
                .map((goal) => ({
                  label: t(getDonationGoalName(goal as DonationGoal)),
                  value: goal as number,
                })),
            }}
          />
        </Col>
        <Col span={8}>
          <FieldBuilder
            name="charityId"
            label={t("charity")}
            width="large"
            input={{
              type: "select",
              loading: loadingCharities,
              options: charities,
              allowSearch:true,
            }}
          />
        </Col>
        <Col span={8}>
          <FieldBuilder
            name="categoryId"
            label={t("category")}
            width="large"
            input={{
              loading: loadingCategories,
              type: "select",
              options: categories,
              allowSearch: true,
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default CampaignFilter;